import { useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '@/api/client';
import { opsApi } from '@/api/ops';
import type { EnvironmentTenant } from '@/api/opsTypes';
import { PERMISSIONS, type AccountDetail, type Health } from '@/api/types';
import { hasPermission } from '@/auth/session';
import { Callout } from '@/components/Callout';
import { StatusChip } from '@/components/ops/Badges';
import { KeyValues, PageHeader, Section, SubTabs, type SubTab } from '@/components/ops/Layout';
import { ConfirmDialog } from '@/components/ops/Modal';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatBytes, formatCores, formatTimestamp } from '@/ops/format';
import { REPLICA } from '@/ops/live';
import { AccountBillingTab } from '@/pages/AccountBillingTab';
import { AccountHealthSection, AccountUsageSection, PeopleTab } from '@/pages/AccountDetailPage';
import { formatWhen } from '@/pages/AccountsPage';
import { ApiKeysTab } from './tenant/ApiKeysTab';
import { BillingAdminSections } from './tenant/BillingAdminSections';
import { CostsTab } from './tenant/CostsTab';
import { CreditsCard } from './tenant/CreditsCard';
import { DeleteTenantButton } from './tenant/DeleteTenant';
import { MessagingTab } from './tenant/MessagingTab';
import { SetupCheckCard } from './tenant/SetupCheckCard';
import { UsageFootprintTab } from './tenant/UsageFootprintTab';
import { useCurrencyDigits } from '@/ops/currencies';

export type TenantTab =
  | 'overview' | 'usage' | 'people' | 'health' | 'billing' | 'costs' | 'credits' | 'messaging' | 'integrations';

/** Tab keys that older links carry. */
const TAB_ALIASES: Record<string, TenantTab> = { keys: 'integrations' };

/** The tabs a role sees — each one only when the role can read what it shows. */
export function tenantTabs(can: (permission: string) => boolean): SubTab<TenantTab>[] {
  return [
    { key: 'overview', label: 'Overview' },
    { key: 'usage', label: 'Usage' },
    { key: 'people', label: 'People', hidden: !can(PERMISSIONS.analyticsRead) },
    { key: 'health', label: 'Health', hidden: !can(PERMISSIONS.telemetryRead) },
    { key: 'billing', label: 'Billing', hidden: !can(PERMISSIONS.subscriptionRead) && !can(PERMISSIONS.billingRead) },
    { key: 'costs', label: 'Costs', hidden: !can(PERMISSIONS.costsRead) },
    { key: 'credits', label: 'Credits', hidden: !can(PERMISSIONS.costsRead) },
    { key: 'messaging', label: 'Messaging', hidden: !can(PERMISSIONS.messagingRead) },
    { key: 'integrations', label: 'API keys & gateways', hidden: !can(PERMISSIONS.billingRead) },
  ];
}

/**
 * THE tenant page (2026-09-30 redesign — the account page and the environment tenant page merged). The header,
 * Overview, Usage, People and Health read the console's saved copy (they render with the platform down, each saying how
 * old it is); Costs, Credits, Billing, Messaging and API keys are live calls through the console's proxies — each
 * degrades to its own labelled panel (CP2).
 */
export function TenantDetailPage() {
  const { env = '', tenantId = '' } = useParams();
  useCurrencyDigits(env);
  const [params, setParams] = useSearchParams();

  const tabs = tenantTabs(hasPermission);
  const raw = params.get('tab');
  const requested = raw ? TAB_ALIASES[raw] ?? (raw as TenantTab) : null;
  const tab: TenantTab = tabs.some((t) => t.key === requested && !t.hidden) ? requested! : 'overview';
  const setTab = (key: TenantTab) =>
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    }, { replace: true });

  const tenant = useQuery({
    queryKey: ['ops', 'env-tenant', env, tenantId],
    queryFn: () => opsApi.tenants.get(env, tenantId),
    ...REPLICA,
  });
  const canAnalytics = hasPermission(PERMISSIONS.analyticsRead);
  const account = useQuery({
    queryKey: ['account', tenantId],
    queryFn: () => api.account(tenantId),
    enabled: canAnalytics,
    ...REPLICA,
  });

  return (
    <RequirePermission permission={PERMISSIONS.hostsRead} what="Tenant details">
      <QueryState
        query={tenant}
        notFound={
          <div className="callout callout-warning" role="status">
            <span className="callout-icon" aria-hidden="true">!</span>
            <div className="t-body">
              <strong className="t-heading" style={{ display: 'block' }}>No such tenant on {env}</strong>
              The saved copy has no tenant <span className="mono">{tenantId}</span> on this environment. It may belong to
              another environment, or the collector has not run since it was created. <Link to="/ops/tenants">All tenants</Link>
            </div>
          </div>
        }
      >
        {(t) => (
          <>
            <PageHeader
              back={{ to: '/ops/tenants', label: 'All tenants' }}
              title={t.name || '(unnamed)'}
              subtitle={
                <span className="row">
                  <span>{t.environment}</span>
                  <span>·</span>
                  <span>{t.plan_code ? `${t.plan_code} plan` : 'no plan'}</span>
                  {t.subscription_status && <StatusChip status={t.subscription_status} />}
                  {t.is_internal && <StatusChip status="internal" tone="muted" />}
                  <span className="muted">· saved copy synced {formatWhen(t.synced_at)}</span>
                </span>
              }
              actions={<DeleteTenantButton env={t.environment} tenantId={t.tenant_id} name={t.name || '(unnamed)'} />}
            />
            <SubTabs tabs={tabs} active={tab} onChange={setTab} label="Tenant sections" />

            {tab === 'overview' && <OverviewTab tenant={t} account={account.data} canAnalytics={canAnalytics} />}
            {tab === 'usage' && (
              <div className="stack">
                {canAnalytics && (
                  <AccountPart query={account} what="Product usage">
                    {(detail) => <AccountUsageSection detail={detail} />}
                  </AccountPart>
                )}
                <UsageFootprintTab env={env} tenantId={tenantId} />
              </div>
            )}
            {tab === 'people' && (
              <AccountPart query={account} what="People">
                {(detail) => <PeopleTab users={detail.users} tenantId={tenantId} />}
              </AccountPart>
            )}
            {tab === 'health' && <AccountHealthSection tenantId={tenantId} />}
            {tab === 'billing' && (
              <div className="stack">
                {hasPermission(PERMISSIONS.subscriptionRead) && <AccountBillingTab tenantId={tenantId} environment={env} />}
                {hasPermission(PERMISSIONS.billingRead) && <BillingAdminSections env={env} tenantId={tenantId} />}
              </div>
            )}
            {tab === 'costs' && <CostsTab env={env} tenantId={tenantId} />}
            {tab === 'credits' && <CreditsCard env={env} tenantId={tenantId} />}
            {tab === 'messaging' && <MessagingTab env={env} tenantId={tenantId} />}
            {tab === 'integrations' && <ApiKeysTab env={env} tenantId={tenantId} />}
          </>
        )}
      </QueryState>
    </RequirePermission>
  );
}

/** The account-analytics part of the page: loading, "not in the saved copy yet", or the section. */
function AccountPart({
  query, what, children,
}: {
  query: { data?: AccountDetail; isPending: boolean; isError: boolean };
  what: string;
  children: (detail: AccountDetail) => ReactNode;
}) {
  if (query.isPending) return <div className="empty-state"><span className="spinner" aria-label="Loading" /></div>;
  if (query.isError || !query.data) {
    return <div className="empty-state t-body">{what} could not be read from the saved copy — the collector may not have reached this tenant yet.</div>;
  }
  return <>{children(query.data)}</>;
}

function OverviewTab({ tenant: t, account, canAnalytics }: { tenant: EnvironmentTenant; account?: AccountDetail; canAnalytics: boolean }) {
  const f = t.footprint;
  const canHealth = hasPermission(PERMISSIONS.telemetryRead);
  const health = useQuery({
    queryKey: ['tenant-health', t.tenant_id],
    queryFn: () => api.scores.health(t.tenant_id),
    enabled: canHealth,
    retry: false,
  });
  const a = account?.account;
  const unusedApps = a ? a.apps_enabled.filter((x) => !a.apps_used.includes(x)) : [];

  return (
    <div className="stack">
      <div className="kpi-row">
        <Tile
          label="People"
          value={t.user_count === null ? '?' : t.user_count.toLocaleString('en-US')}
          sub={t.user_count === null ? 'could not be counted' : `${t.active_user_count ?? '?'} active`}
        />
        <Tile
          label="Data stored"
          value={f?.db_bytes == null ? '—' : formatBytes(f.db_bytes + (t.storage_used_bytes ?? 0))}
          sub={f?.db_bytes == null
            ? 'databases not measured yet'
            : `${formatBytes(f.db_bytes)} databases · ${t.storage_used_bytes === null ? 'files not measured' : `${formatBytes(t.storage_used_bytes)} files`}`}
        />
        <Tile
          label="Server use (estimated)"
          value={f?.cpu_cores.value == null ? '—' : formatCores(f.cpu_cores.value)}
          sub={f?.memory_bytes.value == null
            ? `not enough samples in the last ${f?.period_days ?? 7} days`
            : `${formatBytes(f.memory_bytes.value)} memory · ${f.period_days}-day average share`}
        />
        {canHealth && (
          <Tile
            label="Health"
            value={health.data ? (health.data.unscored ? 'n/a' : Math.round(health.data.total).toString()) : '—'}
            sub={healthSub(health.data, health.error)}
          />
        )}
      </div>

      {unusedApps.length > 0 && (
        <Callout tone="warning" title={`${unusedApps.length} app(s) switched on but never opened`}>
          {unusedApps.join(', ')} — no recorded activity. A failed rollout is worth a call.
        </Callout>
      )}

      <Section title="About this tenant" meta={`saved copy, synced ${formatTimestamp(t.synced_at)}`}>
        <KeyValues
          rows={[
            {
              label: 'Owner',
              value: (
                <>
                  {a
                    ? <>{a.owner_name || '—'}{a.owner_email && <span className="muted"> · {a.owner_email}</span>}</>
                    : canAnalytics ? '—' : 'needs analytics access'}
                  {/* IAM keeps the owner invitation of a tenant the console set up — the only kind it can re-send. */}
                  {t.provisioning_job_id && hasPermission(PERMISSIONS.tenantProvision) && (
                    <ResendOwnerInvitation env={t.environment} tenantId={t.tenant_id} />
                  )}
                </>
              ),
            },
            { label: 'Environment', value: t.environment },
            { label: 'Plan', value: t.plan_code ? <>{t.plan_code} {t.subscription_status && <StatusChip status={t.subscription_status} />}</> : 'no plan on record' },
            { label: 'Apps in use', value: a ? (a.apps_used.length ? a.apps_used.join(', ') : 'none yet') : '—' },
            { label: 'Last activity', value: t.last_activity_at ? `${formatWhen(t.last_activity_at)} (${formatTimestamp(t.last_activity_at)})` : 'never' },
            { label: 'Created', value: formatTimestamp(t.created_at) },
            {
              label: 'Set up by',
              value: t.provisioning_job_id && !hasPermission(PERMISSIONS.tenantProvision) ? (
                <>the console <StatusChip status={t.provisioning_status ?? 'unknown'} /></>
              ) : t.provisioning_job_id ? (
                <Link to={`/ops/provisioning/jobs/${t.provisioning_job_id}`}>
                  the console <StatusChip status={t.provisioning_status ?? 'unknown'} />
                </Link>
              ) : (
                'created outside the console'
              ),
            },
            { label: 'Tenant id', value: <span className="mono t-caption">{t.tenant_id}</span> },
          ]}
        />
      </Section>

      {hasPermission(PERMISSIONS.costsRead) && <SetupCheckCard env={t.environment} tenantId={t.tenant_id} jobId={t.provisioning_job_id} />}
    </div>
  );
}

/** "Resend invitation" next to the owner: a NEW link replaces the previous one (lapsed, lost, sent to the wrong inbox). */
function ResendOwnerInvitation({ env, tenantId }: { env: string; tenantId: string }) {
  const [confirming, setConfirming] = useState(false);
  const resend = useMutation({
    mutationFn: () => opsApi.tenants.resendOwnerInvitation(env, tenantId),
    onSuccess: () => setConfirming(false),
  });
  const outcome = resend.data
    ? resend.data.sent
      ? 'A new invitation was e-mailed to the owner.'
      : resend.data.reason === 'nothing_to_send'
        ? 'Nothing to send: the owner already signed up (or declined the invitation).'
        : 'Nothing to send: this tenant has no owner invitation on record.'
    : null;
  return (
    <>
      {' '}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => { resend.reset(); setConfirming(true); }}>
        Resend invitation
      </button>
      {outcome && <span className="t-caption muted" data-testid="owner-invitation-outcome"> {outcome}</span>}
      {confirming && (
        <ConfirmDialog title="E-mail the owner a new invitation?" confirmLabel="Send new invitation" pending={resend.isPending}
          onConfirm={() => resend.mutate()} onCancel={() => { setConfirming(false); resend.reset(); }}>
          <p className="t-body" style={{ margin: 0 }}>
            The owner gets a new link to set their password and sign in. The link sent before stops working.
          </p>
          <MutationError error={resend.error} />
        </ConfirmDialog>
      )}
    </>
  );
}

function healthSub(health: Health | undefined, error: unknown): string {
  // Only a 404 means "no score yet"; any other failure is a read that did not work — never presented as "not scored".
  if (error && !health) return error instanceof ApiError && error.status === 404 ? 'not scored yet' : 'could not be read';
  if (!health) return 'loading…';
  if (health.unscored) return 'nothing measurable yet';
  return `${Math.round(health.coverage * 100)}% of the score measured`;
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card stat-tile">
      <div className="stat-label t-label">{label}</div>
      <div className={`stat-value tabular${value.length > 9 ? ' is-long' : ''}`}>{value}</div>
      {sub && <div className="stat-sub t-caption">{sub}</div>}
    </div>
  );
}
